import React, { forwardRef } from 'react';
import styles from './ShopProductCardPrimitives.module.css';

type PolymorphicProps = React.HTMLAttributes<HTMLElement> & {
  as?: React.ElementType;
};

export const ShopProductCardRoot = forwardRef<HTMLElement, PolymorphicProps>(
  function ShopProductCardRoot({ className, as, ...rest }, ref) {
    const Tag: any = as || 'div';
    return (
      <Tag
        ref={ref}
        className={[styles.shopProductCardRoot, className]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      />
    );
  },
);

type ShopCardMediaProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: React.ElementType;
  $background?: 'transparent' | 'white';
  $borderRadius?: '12px' | '8px';
};

export const ShopCardMedia = forwardRef<HTMLButtonElement, ShopCardMediaProps>(
  function ShopCardMedia(
    { className, as, $background, $borderRadius, ...rest },
    ref,
  ) {
    const Tag: any = as || 'button';
    return (
      <Tag
        ref={ref}
        type="button"
        data-background={$background}
        data-border-radius={$borderRadius}
        className={[styles.shopCardMedia, className].filter(Boolean).join(' ')}
        {...rest}
      />
    );
  },
);

/**
 * Full-card dim/blur layer: render as a DIRECT CHILD of {@link ShopProductCardRoot}
 * (not inside the media) so it covers the whole card — image + body — behind an open
 * bottom sheet. Add class `open` when visible. Sits below the sheet (z-index 1 < 2/3).
 */
export const ShopCardMediaDimBackdrop = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ShopCardMediaDimBackdrop({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={[styles.shopCardMediaDimBackdrop, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
});

/** Merch / bundles: grey bottom sheet shell; add classes `sizeOverlay` (contract) and `open` when visible */
export const ShopCardBottomSheet = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ShopCardBottomSheet({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={[styles.shopCardBottomSheet, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
});

export const ShopCardBody = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ShopCardBody({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={[styles.shopCardBody, className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

/** Title + description + spacer (e.g. `<br />`) — same visual rules as legacy `.acessSpec` / `.ringSpec`. */
export const ShopCardSpecSection = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ShopCardSpecSection({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={[styles.shopCardSpecSection, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
});

export const ShopCardFooterRow = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ShopCardFooterRow({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={[styles.shopCardFooterRow, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
});

export const ShopCardPriceCluster = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ShopCardPriceCluster({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={[styles.shopCardPriceCluster, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
});

export const ShopCardPrimaryCta = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function ShopCardPrimaryCta({ className, ...rest }, ref) {
  return (
    <button
      ref={ref}
      className={[styles.shopCardPrimaryCta, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
});

type ShopCardTagProps = React.HTMLAttributes<HTMLSpanElement> & {
  $top?: string;
  $right?: string;
  $heightMode?: 'default' | 'accessories' | 'ring';
};

// Hidden site-wide across /shop. The value stays in the product data
// (reversible); only the badge render is suppressed.
const HIDDEN_SHOP_CARD_TAGS = new Set(['new', 'popular']);

/**
 * Product-card corner tag. Renders nothing when the tag is a hidden one
 * ('new'/'popular', case-insensitive); every other tag (e.g. 'sold out') still
 * renders. Non-string children always render.
 */
export const ShopCardTag = forwardRef<HTMLSpanElement, ShopCardTagProps>(
  function ShopCardTag(
    { className, $top, $right, $heightMode, style, children, ...rest },
    ref,
  ) {
    if (
      typeof children === 'string' &&
      HIDDEN_SHOP_CARD_TAGS.has(children.trim().toLowerCase())
    ) {
      return null;
    }
    const mergedStyle: React.CSSProperties | undefined =
      $top != null || $right != null
        ? {
            ...style,
            ...($top != null ? { ['--top' as any]: $top } : {}),
            ...($right != null ? { ['--right' as any]: $right } : {}),
          }
        : style;
    return (
      <span
        ref={ref}
        data-testid="shop-card-tag"
        data-height-mode={$heightMode}
        style={mergedStyle}
        className={[styles.shopCardTag, className].filter(Boolean).join(' ')}
        {...rest}
      >
        {children}
      </span>
    );
  },
);

/** Flip-on-hover image wrapper shared by Accessories and TravelCharger. */
export const ShopCardFlipImg = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ShopCardFlipImg({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={[styles.shopCardFlipImg, className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

/**
 * @deprecated Alias of {@link ShopCardMedia}. The plan-tile image aspect is now
 * the shared default for every card media, so this is identical to ShopCardMedia.
 * Kept for back-compat with existing imports; prefer ShopCardMedia in new code.
 */
export const ShopCardPlanTileMedia = ShopCardMedia;
